import React from 'react';
import SecondaryButton from '@/components/common/buttons/SecondaryButton';
import { Box, Flex, HStack, Input, VStack } from '@chakra-ui/react';
import { IconButton } from '@/components/ui/icon-button';
import { DocumentWithDownloadUrl as Document } from '@/client';
import BaseDocumentEdit from './BaseDocumentEdit';
import { parseLinkDescription } from '@/utils/linkUtils';
import { Field } from '@/components/ui/field';
import { IoOpenOutline } from 'react-icons/io5';
import { useForm } from 'react-hook-form';
import useUpdateTextRecord from '@/hooks/house/documents/useUpdateTextRecord';
import { Title } from '@/components/ui/title';

interface DocumentLinkEditProps {
	document: Document;
	onDocumentUpdated: () => void;
	onVisibilityChange?: (isPrivate: boolean) => void;
	/** True when the link is a pending share — hides the in-body privacy toggle (privacy is chosen at accept time). */
	isPendingShare?: boolean;
}

interface LinkFormValues {
	title: string;
	url: string;
}

/**
 * Component for editing link documents
 * Uses useForm for form management with internal update button
 */
const DocumentLinkEdit: React.FC<DocumentLinkEditProps> = (props) => {
	const { document, onDocumentUpdated, onVisibilityChange, isPendingShare = false } = props;
	// Parse the initial link data from document description
	const initialLinkData = parseLinkDescription(document.description);

	const {
		register,
		handleSubmit,
		watch,
		formState: { isValid, isDirty },
	} = useForm<LinkFormValues>({
		mode: 'onChange',
		defaultValues: {
			title: initialLinkData.title || '',
			url: initialLinkData.url || '',
		},
	});

	/** Watch the URL field for the open link functionality */
	const watchedUrl = watch('url');

	const { updateDocument, isUpdating } = useUpdateTextRecord<LinkFormValues>({
		document,
		transformData: (data) => {
			const linkData = {
				title: data.title.trim() || null,
				url: data.url.trim(),
			};
			return JSON.stringify(linkData);
		},
	});

	const onSubmit = async (data: LinkFormValues) => {
		await updateDocument(data);
		onDocumentUpdated();
	};

	const handleOpenLink = () => {
		if (watchedUrl) {
			window.open(watchedUrl, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<BaseDocumentEdit document={document} onVisibilityChange={onVisibilityChange} isPendingShare={isPendingShare}>
			<VStack gap={6} align="stretch" as="form" onSubmit={handleSubmit(onSubmit)}>
				<HStack justify="space-between">
					<Title>Link details</Title>
					<IconButton
						size="md"
						aria-label="Open link"
						variant="ghost"
						onClick={handleOpenLink}
						disabled={!watchedUrl}
					>
						<IoOpenOutline />
					</IconButton>
				</HStack>

				<Field label="Title (optional)">
					<Input
						{...register('title')}
						placeholder="Enter a title for this link"
						size="md"
						borderRadius="md"
						borderColor="primary/12"
						_focus={{
							borderColor: 'focus.ring',
							boxShadow: 'none',
						}}
						disabled={isUpdating}
					/>
				</Field>

				<Box position="relative" mt={2}>
					<Field label="URL" required>
						<Input
							{...register('url', {
								required: 'URL is required',
								validate: (value) => value.trim() !== '' || 'URL cannot be empty',
							})}
							placeholder="example.com"
							size="md"
							borderRadius="md"
							borderColor="primary/12"
							_focus={{
								borderColor: 'focus.ring',
								boxShadow: 'none',
							}}
							disabled={isUpdating}
						/>
					</Field>
				</Box>
				<Flex w="100%" justify="flex-end" mt={2}>
					<SecondaryButton
						type="submit"
						loading={isUpdating}
						loadingText="Updating..."
						disabled={!isValid || !isDirty || isUpdating}
					>
						Update
					</SecondaryButton>
				</Flex>
			</VStack>
		</BaseDocumentEdit>
	);
};

export default DocumentLinkEdit;
